import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BarChart } from '@/components/charts/bar-chart';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { SimulationRunContainerCountData } from '@/routes/(app)/(scenarios)/(simulation)/simulation.types';
import { mapContainerCountChartData } from '@/routes/(app)/(scenarios)/(simulation)/simulation.utils';

interface SimulationContainersCountChartProps {
  data: SimulationRunContainerCountData;
}
export const SimulationContainersCountChart: FC<
  SimulationContainersCountChartProps
> = ({ data }) => {
  const { t } = useTranslation();
  const [showRegular, setShowRegular] = useState(true);
  const [showReefer, setShowReefer] = useState(true);

  const chartData = useMemo(
    () => mapContainerCountChartData({ data, showRegular, showReefer }),
    [data, showRegular, showReefer]
  );

  const exportFilters = useMemo(
    () => [
      { label: t('general.regular'), active: showRegular },
      { label: t('general.reefer'), active: showReefer },
    ],
    [t, showRegular, showReefer]
  );

  const customControlElement = (
    <div className='flex items-center gap-4 pt-1'>
      <Label className='cursor-pointer'>
        <Checkbox
          checked={showRegular}
          onCheckedChange={(checked: boolean | 'indeterminate') => {
            setShowRegular(checked === true);
          }}
        />
        {t('general.regular')}
      </Label>
      <Label className='cursor-pointer'>
        <Checkbox
          checked={showReefer}
          onCheckedChange={(checked: boolean | 'indeterminate') => {
            setShowReefer(checked === true);
          }}
        />
        {t('general.reefer')}
      </Label>
    </div>
  );

  return (
    <BarChart
      className='col-span-1'
      title={t('simulation.charts.containersCount.title')}
      data={chartData}
      showTabTotals={false}
      indexKey='date'
      customControlElement={customControlElement}
      exportFilters={exportFilters}
      tabs={[
        { key: 'total', label: t('general.total') },
        { key: 'incoming', label: t('general.incoming') },
        { key: 'outgoing', label: t('general.outgoing') },
      ]}
      fields={[
        {
          key: 'import',
          label: t('general.import'),
          color: 'red',
          stackId: 'a',
        },
        {
          key: 'export',
          label: t('general.export'),
          color: 'blue',
          stackId: 'a',
        },
        {
          key: 'transshipment',
          label: t('general.transshipment'),
          color: 'green',
          stackId: 'a',
        },
      ]}
    />
  );
};
